<script lang="ts" setup>
import { ref, computed } from "vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import OrganizationSwitchModal from "@/views/shared/components/modals/OrganizationSwitchModal.vue";
import type { PartnerIssue } from "@/types/project-types";
import type { Config } from "datatables.net";
import {
  usePartnerIssues,
  useResolvePartnerIssue,
  useReopenPartnerIssue,
  issueFixRoute,
  issueIconClass,
  issueSeverityColor,
  issueSeverityBadgeClass,
  formatIssueTimeAgo,
  ISSUE_TYPE_OPTIONS,
  ISSUE_SEVERITY_OPTIONS,
} from "@/queries/partner-issues";

// Live data — server scopes to the caller-partner's active clients.
const { issues, issuesLoading: isLoading } = usePartnerIssues({ take: 200 });
const { resolveIssueAsync, resolveIssuePending } = useResolvePartnerIssue();
const { reopenIssueAsync, reopenIssuePending } = useReopenPartnerIssue();

const showSwitchModal = ref(false);
const selectedIssue = ref<PartnerIssue | null>(null);

// Resolve modal
const showResolveModal = ref(false);
const resolveTarget = ref<PartnerIssue | null>(null);
const resolveNote = ref("");

// Filters
const searchQuery = ref("");
const selectedClient = ref("");
const selectedProject = ref("");
const selectedSeverity = ref("");
const selectedType = ref("");
const selectedStatus = ref("unresolved");
const dateFrom = ref("");
const dateTo = ref("");

// Computed values
const uniqueClients = computed(() => {
  const clients = [...new Set(issues.value.map((i) => i.clientName).filter(Boolean))];
  return clients.sort();
});

const uniqueProjects = computed(() => {
  const projects = [...new Set(issues.value.map((i) => i.projectName).filter(Boolean))];
  return projects.sort();
});

const issueStatuses = [
  { value: "unresolved", label: "Unresolved Only" },
  { value: "resolved", label: "Resolved Only" },
  { value: "all", label: "All Issues" },
];

const filteredIssues = computed(() => {
  let filtered = issues.value;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      (i) =>
        i.title.toLowerCase().includes(query) ||
        i.description.toLowerCase().includes(query) ||
        i.clientName?.toLowerCase().includes(query) ||
        i.projectName?.toLowerCase().includes(query)
    );
  }

  if (selectedClient.value) {
    filtered = filtered.filter((i) => i.clientName === selectedClient.value);
  }

  if (selectedProject.value) {
    filtered = filtered.filter((i) => i.projectName === selectedProject.value);
  }

  if (selectedSeverity.value) {
    filtered = filtered.filter((i) => i.severity === selectedSeverity.value);
  }

  if (selectedType.value) {
    filtered = filtered.filter((i) => i.type === selectedType.value);
  }

  if (selectedStatus.value && selectedStatus.value !== "all") {
    const isResolved = selectedStatus.value === "resolved";
    filtered = filtered.filter((i) => i.resolved === isResolved);
  }

  if (dateFrom.value) {
    filtered = filtered.filter(
      (i) => new Date(i.dateOccurred) >= new Date(dateFrom.value)
    );
  }

  if (dateTo.value) {
    filtered = filtered.filter(
      (i) => new Date(i.dateOccurred) <= new Date(dateTo.value)
    );
  }

  return filtered;
});

const criticalCount = computed(
  () => issues.value.filter((i) => i.severity === "critical" && !i.resolved).length
);

const unresolvedCount = computed(
  () => issues.value.filter((i) => !i.resolved).length
);

// Methods
function clearFilters() {
  searchQuery.value = "";
  selectedClient.value = "";
  selectedProject.value = "";
  selectedSeverity.value = "";
  selectedType.value = "";
  selectedStatus.value = "unresolved";
  dateFrom.value = "";
  dateTo.value = "";
}

function formatDateTime(timestamp: string) {
  return new Date(timestamp).toLocaleString(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

// Dispatch row clicks by which action button was hit (DataTables renders the row as HTML, so we
// read the `data-action` off the click target). A click anywhere else on the row opens the
// switch-to-fix modal, preserving the previous default behaviour.
function handleIssueClick(issue: PartnerIssue, _index: number, event: Event) {
  const actionEl = (event.target as HTMLElement)?.closest("[data-action]");
  const action = actionEl?.getAttribute("data-action");

  if (action === "resolve") {
    openResolveModal(issue);
    return;
  }
  if (action === "reopen") {
    handleReopen(issue);
    return;
  }
  // "fix" or a bare row click → switch into the client org.
  selectedIssue.value = issue;
  showSwitchModal.value = true;
}

// Open the "switch into client org to fix" modal directly (used by the mobile card's Fix button;
// the desktop table routes through handleIssueClick's data-action dispatch).
function openSwitchModal(issue: PartnerIssue) {
  selectedIssue.value = issue;
  showSwitchModal.value = true;
}

function handleSwitchCancelled() {
  selectedIssue.value = null;
}

// Route the partner to the tab most relevant to the issue (run history / connections / wizard)
// after switching org — see issueFixRoute. Falls back to home when there's no project.
const switchTargetRoute = computed(() =>
  selectedIssue.value ? issueFixRoute(selectedIssue.value) : { name: "home" },
);

const actionError = ref<string | null>(null);

function openResolveModal(issue: PartnerIssue) {
  resolveTarget.value = issue;
  resolveNote.value = "";
  actionError.value = null;
  showResolveModal.value = true;
}

async function confirmResolve() {
  if (!resolveTarget.value || !resolveNote.value.trim()) return;
  actionError.value = null;
  try {
    await resolveIssueAsync({ id: resolveTarget.value.id, note: resolveNote.value.trim() });
    showResolveModal.value = false;
    resolveTarget.value = null;
    resolveNote.value = "";
  } catch (e) {
    // Keep the modal open and surface the failure instead of silently doing nothing.
    actionError.value = (e as Error)?.message ?? "Failed to resolve the issue.";
  }
}

async function handleReopen(issue: PartnerIssue) {
  actionError.value = null;
  try {
    await reopenIssueAsync(issue.id);
  } catch (e) {
    actionError.value = (e as Error)?.message ?? "Failed to reopen the issue.";
  }
}

const tableOptions = {
  columns: [
    { data: "id", title: "ID", visible: false },
    {
      data: "severity",
      title: "Severity",
      render: (data: string) =>
        `<span class="badge text-uppercase ${issueSeverityBadgeClass(data)}">${data}</span>`,
    },
    {
      data: "title",
      title: "Issue",
      width: "30%",
      render: (data: string, _type: string, row: PartnerIssue) => {
        const icon = issueIconClass(row.type);
        const color = issueSeverityColor(row.severity);
        const resolvedHtml = row.resolved && row.resolvedDate
          ? `<div class="small text-success mt-1"><i class="bi bi-check-circle me-1"></i>Resolved ${formatIssueTimeAgo(row.resolvedDate)}</div>`
          : "";
        return `<div class="d-flex align-items-start gap-2">
          <i class="bi ${icon} ${color} fs-5 mt-1"></i>
          <div>
            <div class="fw-medium">${data}</div>
            <small class="text-muted">${row.description}</small>
            ${resolvedHtml}
          </div>
        </div>`;
      },
    },
    {
      data: "clientName",
      title: "Client",
      render: (data: string) =>
        `<span class="badge bg-secondary">${data ?? ""}</span>`,
    },
    {
      data: "projectName",
      title: "Project",
      render: (data: string) =>
        data
          ? `<span class="text-muted"><i class="bi bi-folder me-1"></i>${data}</span>`
          : `<span class="text-muted">-</span>`,
    },
    {
      data: "dateOccurred",
      title: "Occurred",
      render: (data: string) =>
        `<div><small class="text-muted d-block">${formatIssueTimeAgo(data)}</small><small class="text-muted">${formatDateTime(data)}</small></div>`,
    },
    {
      data: "resolved",
      title: "Status",
      render: (data: boolean) =>
        `<span class="badge ${data ? "bg-success" : "bg-danger"}">${data ? "Resolved" : "Open"}</span>`,
    },
    {
      data: null,
      title: "Actions",
      orderable: false,
      width: "200px",
      render: (_data: null, _type: string, row: PartnerIssue) =>
        `<div class="d-flex gap-2">
          ${!row.resolved
            ? `<button class="bb-btn-secondary-cta btn-sm text-nowrap" data-action="fix"><i class="bi bi-wrench me-1"></i>Fix</button>
               <button class="bb-btn-secondary-cta btn-sm text-nowrap" data-action="resolve"><i class="bi bi-check2-circle me-1"></i>Resolve</button>`
            : `<button class="bb-btn-secondary-cta btn-sm text-nowrap" data-action="reopen"><i class="bi bi-arrow-counterclockwise me-1"></i>Reopen</button>`}
        </div>`,
    },
  ],
  createdRow: (row: HTMLElement, data: PartnerIssue) => {
    // Subtle severity accent (left border + faint tint) instead of a heavy solid row fill —
    // mirrors the outlined-card look of the dashboard issues widget. See scoped .issue-row-* CSS.
    if (data.resolved) {
      row.classList.add("issue-row-resolved");
    } else if (data.severity === "critical") {
      row.classList.add("issue-row-critical");
    } else if (data.severity === "high" || data.severity === "medium") {
      row.classList.add("issue-row-warning");
    }
  },
  pageLength: 25,
  layout: {
    topEnd: "",
    bottomEnd: "",
    bottomStart: "info",
  },
} as Config;
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Partner Issues"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Partners', href: '/partners' },
          { label: 'Issues' },
        ]"
      >
        <template #actions>
          <router-link
            to="/partners/activity"
            class="bb-btn-secondary-cta d-none d-md-inline"
          >
            <i class="bi bi-activity me-2"></i>Activity
          </router-link>
          <router-link to="/partners" class="bb-btn-secondary-cta d-none d-md-inline">
            <i class="bi bi-arrow-left me-2"></i>Back to Overview
          </router-link>
        </template>
      </PageHeader>

      <div class="d-flex gap-2 mb-4">
        <span v-if="criticalCount > 0" class="badge bg-danger">
          {{ criticalCount }} Critical
        </span>
        <span class="badge bg-secondary">{{ unresolvedCount }} Unresolved</span>
      </div>

      <!-- Errors from reopen (resolve errors show inside the resolve modal). -->
      <div v-if="actionError && !showResolveModal" class="alert alert-danger d-flex align-items-center mb-4">
        <i class="bi bi-exclamation-triangle me-2"></i>
        <span class="flex-grow-1">{{ actionError }}</span>
        <button type="button" class="btn-close" @click="actionError = null"></button>
      </div>

      <div class="card mb-4">
        <div class="card-body">
          <h2 class="card-title">Filters</h2>
          <div class="row g-3">
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Search</label>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control"
                placeholder="Search issues..."
              />
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Client</label>
              <select v-model="selectedClient" class="form-select">
                <option value="">All Clients</option>
                <option
                  v-for="client in uniqueClients"
                  :key="client"
                  :value="client"
                >
                  {{ client }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Project</label>
              <select v-model="selectedProject" class="form-select">
                <option value="">All Projects</option>
                <option
                  v-for="project in uniqueProjects"
                  :key="project"
                  :value="project"
                >
                  {{ project }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Severity</label>
              <select v-model="selectedSeverity" class="form-select">
                <option value="">All Severities</option>
                <option
                  v-for="severity in ISSUE_SEVERITY_OPTIONS"
                  :key="severity.value"
                  :value="severity.value"
                >
                  {{ severity.label }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Type</label>
              <select v-model="selectedType" class="form-select">
                <option value="">All Types</option>
                <option
                  v-for="type in ISSUE_TYPE_OPTIONS"
                  :key="type.value"
                  :value="type.value"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Status</label>
              <select v-model="selectedStatus" class="form-select">
                <option
                  v-for="status in issueStatuses"
                  :key="status.value"
                  :value="status.value"
                >
                  {{ status.label }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">From Date</label>
              <input v-model="dateFrom" type="date" class="form-control" />
            </div>
            <div class="col-12 col-md-6 col-lg-3 d-flex align-items-end">
              <button @click="clearFilters" class="bb-btn-secondary-cta w-100">
                <i class="bi bi-x-circle me-1"></i>Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <DataView
        v-else
        :data="filteredIssues"
        :view-mode="'table'"
        :options="tableOptions"
        @row-click="(issue: PartnerIssue, index: number, event: Event) => handleIssueClick(issue, index, event)"
      >
        <!-- Mobile card — outlined card with a severity-coloured border, matching the dashboard
             issues widget (IssuesList.vue). -->
        <template #grid-card="{ row: issue }">
          <div
            class="card h-100 card-hover"
            :class="{
              'border-danger': !issue.resolved && issue.severity === 'critical',
              'border-warning': !issue.resolved && (issue.severity === 'high' || issue.severity === 'medium'),
              'border-success': issue.resolved,
            }"
          >
            <div class="card-body">
              <div class="d-flex align-items-start gap-3">
                <i
                  :class="[issueIconClass(issue.type), issueSeverityColor(issue.severity)]"
                  class="fs-5 mt-1 flex-shrink-0"
                ></i>
                <div class="flex-grow-1 min-w-0">
                  <div class="d-flex justify-content-between align-items-start gap-2 mb-1">
                    <h4 class="mb-0 fw-medium">{{ issue.title }}</h4>
                    <span class="badge text-uppercase flex-shrink-0" :class="issueSeverityBadgeClass(issue.severity)">
                      {{ issue.severity }}
                    </span>
                  </div>
                  <p class="text-muted small mb-2">{{ issue.description }}</p>
                  <div class="d-flex flex-wrap align-items-center gap-2 small">
                    <span class="badge bg-secondary">{{ issue.clientName }}</span>
                    <span v-if="issue.projectName" class="text-muted">
                      <i class="bi bi-folder me-1"></i>{{ issue.projectName }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-footer bg-transparent border-top-0 d-flex justify-content-between align-items-center">
              <small class="text-muted"><i class="bi bi-clock me-1"></i>{{ formatIssueTimeAgo(issue.dateOccurred) }}</small>
              <div class="d-flex gap-2">
                <template v-if="!issue.resolved">
                  <button class="bb-btn-secondary-cta btn-sm text-nowrap" @click.stop="openSwitchModal(issue)">
                    <i class="bi bi-wrench me-1"></i>Fix
                  </button>
                  <button class="bb-btn-secondary-cta btn-sm text-nowrap" @click.stop="openResolveModal(issue)">
                    <i class="bi bi-check2-circle me-1"></i>Resolve
                  </button>
                </template>
                <button v-else class="bb-btn-secondary-cta btn-sm text-nowrap" @click.stop="handleReopen(issue)">
                  <i class="bi bi-arrow-counterclockwise me-1"></i>Reopen
                </button>
              </div>
            </div>
          </div>
        </template>

        <template #empty-state>
          <div class="text-center py-5">
            <i class="bi bi-check-circle display-1 text-success"></i>
            <h3 class="mt-3 text-muted">No issues found</h3>
            <p class="text-muted">Try adjusting your filters to see results.</p>
          </div>
        </template>
      </DataView>
    </PanelCard>
  </PanelGrid>

  <OrganizationSwitchModal
    :show="showSwitchModal"
    @update:show="showSwitchModal = $event"
    :target-organization="{
      id: selectedIssue?.clientId || '',
      name: selectedIssue?.clientName || '',
    }"
    :target-route="switchTargetRoute"
    context="issue"
    @cancelled="handleSwitchCancelled"
  />

  <!-- Resolve modal — a note is required to resolve. -->
  <div
    v-if="showResolveModal"
    class="modal fade show d-block"
    tabindex="-1"
    style="background: rgba(0, 0, 0, 0.5)"
    @click.self="showResolveModal = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Resolve Issue</h5>
          <button type="button" class="btn-close" @click="showResolveModal = false"></button>
        </div>
        <div class="modal-body">
          <p class="mb-2 fw-medium">{{ resolveTarget?.title }}</p>
          <label class="form-label">Resolution note <span class="text-danger">*</span></label>
          <textarea
            v-model="resolveNote"
            class="form-control"
            rows="3"
            placeholder="What did you do to resolve this? (required)"
          ></textarea>
          <div v-if="actionError" class="alert alert-danger mt-3 mb-0 small">
            <i class="bi bi-exclamation-triangle me-1"></i>{{ actionError }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="bb-btn-secondary-cta" @click="showResolveModal = false">Cancel</button>
          <button
            class="bb-btn-primary-cta"
            :disabled="!resolveNote.trim() || resolveIssuePending"
            @click="confirmResolve"
          >
            <i class="bi bi-check2-circle me-1"></i>Resolve
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Severity accent for desktop table rows — a coloured left border + a faint tint, replacing the
// previous heavy solid `table-danger/warning/success` fills. Matches the outlined-card look of the
// dashboard issues widget. Rows live in DataView's DataTable DOM, so reach them with :deep().
:deep(tr.issue-row-critical) > td:first-child {
  border-left: 3px solid var(--bs-danger);
}
:deep(tr.issue-row-warning) > td:first-child {
  border-left: 3px solid var(--bs-warning);
}
:deep(tr.issue-row-resolved) > td:first-child {
  border-left: 3px solid var(--bs-success);
}

:deep(tr.issue-row-critical) > td {
  background-color: rgba(var(--bs-danger-rgb), 0.04);
}
:deep(tr.issue-row-warning) > td {
  background-color: rgba(var(--bs-warning-rgb), 0.05);
}
:deep(tr.issue-row-resolved) > td {
  background-color: rgba(var(--bs-success-rgb), 0.04);
}
</style>
